import React, { FC, ReactNode, useState } from 'react';
import { Anohana, Ava, Btn, Bx, CheckboxControl, IconAva, IImageGalleryProps, Link, Typo } from '@curry-group/mui-curcuma';
import { Grid, Menu, MenuItem } from '@material-ui/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarAlt, faCameraHome } from '@fortawesome/pro-light-svg-icons';
import { IConferenceInformation, IMessageModel, IMessageReferences } from '../../../model/communication/Message';
import useTheme from '@material-ui/core/styles/useTheme';
import { MessageQuote } from './quote';
import { assetUrl, getDetailRoute, getMessageRoute, getProfileRouteByUserId, getUniqueArray, hashCode, ListAvatarColors, renderListItem } from '../../../helper';
import { AssetAttachment } from '../attachments';
import { MessageInteractionBar } from './interaction-bar';
import { MessageHeader } from './header';
import { MessageContent } from './content';
import { IAsset, isImage } from '../../../model/ryve/Asset';
import { MessageImageGallery } from './gallery';
import { useIsMobile } from '../../../helper/hooks/isMobile';
import { ChatMessage } from '../../chat-message';
import makeStyles from '@material-ui/core/styles/makeStyles';
import clsx from 'clsx';
import { useLocation } from 'react-router';
import { useSelector } from 'react-redux';
import { IThing, transformForList } from '../../../model/ryve/Thing';
import { ISearchEntry } from '../../../model/search/SearchEntry';
import { FormInput } from '../../form/controls/input';
import { AvatarWithBadge } from '../../user-profile/avatarWithBadge';
import { RoleType } from '../../../model/communication/Role';
import _ from 'lodash';
import { PinMessagePopup } from './pin-message-popup/pin-message-popup';

export interface IMessageProps {
  parentId: string;
  id: string;
  avatarSrc?: string;
  name: string;
  timestamp: string;
  modified?: boolean;
  content: string;
  deleted?: boolean;
  width?: number;
  attachments?: IAsset[];
  createdBy: string;

  attachedThing?: IThing<ISearchEntry>; 

  isOwnMessage: boolean;

  /** Render the content as markdown (bot answers use #, ** and inline links). */
  markdown?: boolean;

  canEdit?: boolean;
  onEditClicked?: () => void;
  canDelete?: boolean;
  onDeleteClicked?: () => void;

  quotes?: IMessageModel;
  allowQuotes?: boolean;
  quoteClicked?: () => void;
  emoteClicked?: (emoji:string) => void;

  allowThreading?: boolean;
  numThreadMessages?: number;
  threadingClicked?: () => void;
  asInlineThread?: boolean;

  allowVoting?: boolean;
  userVoting?: number;
  voteUpClick?: () => void;
  voteDownClick?: () => void;
  numVotes?: number;

  onMessageRead?: () => void;
  onJoinConference?: (message?: IMessageModel) => void;
  abbreviation?: string;
  conferenceInfo?: IConferenceInformation;
  references?: IMessageReferences;
  onReferenceSelected?: (type: string, data: any) => void;

  emotes?: {};
  activeParticipants?: any;

  newestAnswer: IMessageModel | null;
  userStatus?: string;

  shareAllowed?: boolean;
  shareClicked?: () => void;
  alertClicked?: (alertType: string, alertContent: string) => void;

  /**
   * Overrides the default letter/image avatar when provided.
   * Used e.g. for bot-chat messages to render an icon avatar instead of initials.
   */
  avatarNode?: ReactNode;
}

export const Message: React.FC<IMessageProps> = ({
  parentId,
  id,
  markdown,
  width,
  avatarSrc,
  name,
  timestamp,
  modified,
  attachments,
  attachedThing,
  content,
  deleted,
  isOwnMessage,
  allowThreading,
  allowVoting,
  voteUpClick,
  userVoting,
  voteDownClick,
  threadingClicked,
  numThreadMessages,
  asInlineThread,
  quotes,
  numVotes,
  allowQuotes,
  quoteClicked,
  emoteClicked,
  canDelete,
  onDeleteClicked,
  canEdit,
  onEditClicked,
  onMessageRead,
  onJoinConference,
  abbreviation,
  createdBy,
  conferenceInfo,
  references,
  onReferenceSelected,
  emotes,
  activeParticipants,
  newestAnswer,
  userStatus,
  shareAllowed,
  shareClicked,
  alertClicked,
  avatarNode,
}) => {
  const theme = useTheme();
  const isMobile = useIsMobile();
  const location = useLocation();
  const appconfig = useSelector(state => state.foundation.appconfig);
  const userId = useSelector(state => state.foundation.profile?.userId);
  const participants = useSelector(state => state.communication?.participants);
  const roles = useSelector(state => state.communication?.communication?.roles);

  const avatarDict = useSelector(state => state.foundation?.avatarsById);
  const [alertModalOpen, setAlertModalOpen] = useState(false);
  const [statement, setStatement] = useState('');
  const [alertType, setAlertType] = useState<string>('infringement');
  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);
  const [pinMessagePopupOpen, setPinMessagePopupOpen] = useState<boolean>(false);

  const participant = participants?.find(p => p.user === userId);
  const participantRoles = _.intersectionWith(roles, (participant?.roles ?? []), (role, roleId) => {
    return role?.type !== RoleType.DEFAULT && role?.roleId === roleId;
  });
  const isAdmin = participantRoles?.find(r => r.type === RoleType.ADMINISTRATOR) !== undefined;

  let bgColor = isOwnMessage ? theme.palette.primary.main : theme.palette.background.paper;
  let color = isOwnMessage ? theme.palette.primary.contrastText : '';

  if (deleted) {
    color = theme.palette.primary.contrastText;
    bgColor = theme.palette.grey[700];
  }

  const threadOffsetMargin = 24;

  const realWidth = Math.min(Math.max(width || 0, 60), 100);

  let boxWidth = '100%';
  let boxMarginLeft = '0px';
  let boxMarginRight = boxMarginLeft;

  if (asInlineThread) {
    boxWidth = `calc(100% - ${threadOffsetMargin * 2}px)`;
    boxMarginLeft = threadOffsetMargin + 'px';
    boxMarginRight = boxMarginLeft;
  }

  const downloadAsset = (asset: IAsset) => {
    if (!asset._id) {
      return;
    }
    var a = document.createElement('a');
    a.href = assetUrl(asset._id) || '';
    a.setAttribute('download', asset.name + asset.extension);
    a.click();
  };
  const [anchorEl, setAnchorEl] = useState<{ target: HTMLElement } | null>(null);
  const [anchorEmote, setAnchorEmote] = useState<{ target: HTMLElement } | null>(null);

  const handleClick = (target: HTMLButtonElement) => {
    setAnchorEl({ target });
  };
  const handleClickEmote = (target: HTMLButtonElement) => {
    setAnchorEmote({ target });
  };
  const handleClose = () => {
    setAnchorEl(null);
  };
  const handleCloseEmote = () => {
    setAnchorEmote(null);
  };

  const hasVoting = allowVoting && !!voteDownClick && !!voteUpClick;
  const hasThreading = (allowThreading || conferenceInfo) && !!threadingClicked;
  const hasQuotes = allowQuotes && !!quoteClicked;

  let hasDropdown = false;
  if (deleted) {
    // hasDropdown = !!(canDelete && onDeleteClicked);
  } else {
    hasDropdown = !!(canDelete && onDeleteClicked) || !!(canEdit && onEditClicked) || !!(!deleted && hasQuotes && !conferenceInfo);
  }

  let galleryImages: IImageGalleryProps['images'] = [];
  if (attachments && attachments.length) {
    galleryImages = attachments
      .filter(attachment => isImage(attachment))
      .map(attachment => {
        return {
          thumbnail: { title: attachment.name, alt: attachment.name, image: assetUrl(attachment._id, true) || '', downloadImage: '' },
          fullImage: { title: attachment.name, alt: attachment.name, image: assetUrl(attachment._id) || '', downloadImage: '' }
        };
      });
  }
  const hash = hashCode(abbreviation ?? '');
  const colorIndex = hash % 4;
  const colorObject = ListAvatarColors[colorIndex];

  const CreatorIcon = (userStatus?: string) => {
    if (isOwnMessage) return <Bx mr={2} pt={0.5} flexGrow={1} flexShrink={0} flexBasis={asInlineThread ? 32 : 46} />;
    const avatar = avatarNode
      ? avatarNode
      : <Ava
        style={{ color: colorObject.color, fontSize: '14px', fontWeight: 500, backgroundColor: colorObject.bg }}
        size={asInlineThread ? 'small' : 'small'}
        src={avatarSrc}
      >
        {abbreviation}
      </Ava>;

    let iconContent = avatarNode
      ? <>{avatar}</>
      : <AvatarWithBadge userStatus={userStatus} avatar={avatar as any} variant='dot'/>;

    if (conferenceInfo) {
      iconContent = (
        <IconAva variant="circular" size={asInlineThread ? 'small' : 'small'} color="secondary" inverted={true}>
          <FontAwesomeIcon icon={faCameraHome} />
        </IconAva>
      );
    }

    if (references && references.meeting) {
      iconContent = (
        <IconAva variant="circular" size={asInlineThread ? 'small' : 'small'} color="secondary" inverted={true}>
          <FontAwesomeIcon icon={faCalendarAlt} />
        </IconAva>
      );
    }
    return (
      <Bx mr={2} pt={0.5}>
        {iconContent}
      </Bx>
    );
  };

  let attachedItems = !!attachedThing
    ? transformForList(
        [attachedThing].map(item => {
          return {
            _source: item,
            _id: item._id,
            _score: 0
          };
        })
      )
    : [];
  let quoteOriginLink = '';
  let quoteOriginTitle = '';
  if (!!quotes && attachedItems.length > 0) {
    const quoteOriginItem = attachedItems[0];
    quoteOriginLink = getDetailRoute(quoteOriginItem, appconfig || {}, location) || '';
    if (!!quoteOriginLink) {
      const appendChar = quoteOriginLink.includes('?') ? '&' : '?';
      quoteOriginLink += `${appendChar}${getMessageRoute(quotes, quoteOriginItem)}`;
    }
    quoteOriginTitle = quoteOriginItem.title || '';
    attachedItems = [];
  }

  const alertMessageSenderDisabled = statement == null || statement.length < 50;
  return (
    <Bx
      id={id}
      display="flex"
      position="relative"
      mb={isMobile ? 2 : 3}
      width={isMobile ? '92%' : boxWidth}
      style={{ marginLeft: isMobile ? (isOwnMessage ? '8%' : undefined) : boxMarginLeft, marginRight: isMobile ? (!isOwnMessage ? '8%' : undefined) : boxMarginRight }}
    >
      {!isMobile && CreatorIcon(userStatus)}
      <Bx width={isMobile ? '100%' : realWidth + '%'} className={'position-relative'}>
      {/* <Bx width={isMobile ? '100%' : realWidth + '%'}> */}
        <ChatMessage px={{ xs: 1.5, md: 2 }} pt={{ xs: 1.5, md: 0.75 }} pb={{ xs: 1, md: 1.25 }} bgcolor={bgColor} color={color}>
          <MessageHeader
            mobile={isMobile}
            isOwnMessage={isOwnMessage}
            isConference={!!conferenceInfo}
            references={references}
            isDeleted={deleted}
            isModified={modified}
            timestamp={timestamp}
            content={content}
            createdById={createdBy}
            createdByName={name}
            hasDropdown={hasDropdown}
            setDropdownAnchor={handleClick}
            isMeeting={!!references?.meeting}
          />

          {/* text content or conference or reference */}
          <MessageContent
            content={content}
            markdown={markdown}
            isOwnMessage={isOwnMessage}
            isDeleted={deleted}
            conferenceInfo={conferenceInfo}
            onJoinConference={onJoinConference}
            references={references}
            onReferenceSelected={onReferenceSelected}
            color={color}
          />

          {/* quote */}
          {!deleted && quotes &&
            <MessageQuote
              message={quotes}
              onJoinConference={() => onJoinConference?.(quotes)}
              references={references}
              onReferenceSelected={onReferenceSelected}
              quoteOriginLink={quoteOriginLink}
              quoteOriginTitle={quoteOriginTitle}
            />
          }

          {/* gallery */}
          {!deleted && !!(galleryImages && galleryImages.length) && <MessageImageGallery images={galleryImages} />}

          {/* all attachments */}
          {!deleted &&
            !!(attachments && attachments.length) &&
            attachments.map((attachment, index) => (
              <AssetAttachment key={'attachment-' + index}
                color={isOwnMessage ? 'text.primary' : undefined}
                mt={1}
                onDownload={downloadAsset}
                asset={attachment}
              />
            ))}
          {!deleted && attachedThing?.content && renderListItem(
            attachedItems[0],
            0,
            appconfig,
            location,
            avatarDict,
            undefined,
            isOwnMessage
          )}
          {(!references?.meeting || (!!conferenceInfo?.ended && !!numThreadMessages && numThreadMessages > 0)) && (
            <MessageInteractionBar
              hasVoting={hasVoting}
              hasThreading={hasThreading}
              hasQuotes={hasQuotes}
              isDeleted={deleted}
              isOwnMessage={isOwnMessage}
              numChildren={numThreadMessages}
              numVotes={numVotes}
              userVoting={userVoting}
              requestThread={() => threadingClicked?.()}
              requestQuote={() => quoteClicked?.()}
              voteUp={() => voteUpClick?.()}
              voteDown={() => voteDownClick?.()}
              newestAnswer={newestAnswer}
              shareAllowed={shareAllowed ?? false}
              shareMessage={shareClicked ?? (() => {})}
            />
          )}
          {!!emotes &&
            <MessageEmoteTeaser
              emotes={emotes}
              openEmotes={handleClickEmote}
            >
              👍💙2
            </MessageEmoteTeaser>}
        </ChatMessage>
      </Bx>
      {hasDropdown && !references?.meeting && (
        <MessageDropdown
          target={anchorEl?.target}
          close={handleClose}
          isDeleted={deleted}
          canDelete={canDelete}
          onDelete={() => {
            handleClose();
            onDeleteClicked?.();
          }}
          canEdit={canEdit}
          onEdit={() => {
            handleClose();
            onEditClicked?.();
          }}
          canQuote={hasQuotes}
          canThread={hasThreading}
          onQuote={() => {
            handleClose();
            quoteClicked?.();
          }}
          onEmote={(emoji:string) => {
            handleClose();
            emoteClicked?.(emoji);
          }}
          onAlert={() =>{
            handleClose();
            setAlertModalOpen(true);
          }}
          isOwnMessage={isOwnMessage}
          isAdmin={isAdmin}
          onPin={() => setPinMessagePopupOpen(true)}
          messageId={id}
        />
      )}
      {!!emotes && <MessageEmotes
        target={anchorEmote?.target}
        emotes={emotes}
        close={handleCloseEmote}
        activeParticipants={activeParticipants}
      />}
      <Anohana open={alertModalOpen} transition="zoom" disableScrollLock fullWidth maxWidth="sm" onClose={() => setAlertModalOpen(false)}>
        <Bx p={2.5}>
          <Bx justifyContent="center" mb={2}>
            <Bx pb={1}>
              <Typo variant="h4" component="span">
                Diesen Beitrag melden
              </Typo>
            </Bx>
            <>
              <Bx pb={1}>
                <Grid container spacing={1}>
                  <Grid key={`alert_message_user_infringement`} item>
                    <MessageAlertTypeControl alertId={'infringement'} alertName={'Rechtsverletzung'} alertType={alertType} setAlertType={setAlertType} />
                  </Grid>
                  <Grid key={`alert_message_user_desinformation`} item>
                    <MessageAlertTypeControl alertId={'desinformation'} alertName={'Falschinformation'} alertType={alertType} setAlertType={setAlertType} />
                  </Grid>
                  <Grid key={`alert_message_user_hate_speech`} item>
                    <MessageAlertTypeControl alertId={'hate_speech'} alertName={'Hassrede'} alertType={alertType} setAlertType={setAlertType} />
                  </Grid>
                  <Grid key={`alert_message_user_explicit_content`} item>
                    <MessageAlertTypeControl alertId={'explicit_content'} alertName={'Sexuelle Inhalte'} alertType={alertType} setAlertType={setAlertType} />
                  </Grid>
                  <Grid key={`alert_message_user_forgery`} item>
                    <MessageAlertTypeControl alertId={'forgery'} alertName={'Gefälschtes Konto'} alertType={alertType} setAlertType={setAlertType} />
                  </Grid>
                  <Grid key={`alert_message_user_hacked`} item>
                    <MessageAlertTypeControl alertId={'hacked'} alertName={'Gehacktes Konto'} alertType={alertType} setAlertType={setAlertType} />
                  </Grid>
                </Grid>
              </Bx>
              <FormInput
                value={statement}
                label={'Meldung'}
                placeholder={'Bitte beschreiben Sie Ihr Anliegen.'}
                minLength={50}
                maxLength={2500}
                multiline={true}
                onChange={val => {
                  setStatement(val);
                }}
                lazy
              />
            </>
          </Bx>
          <Bx display="flex" mt={3}>
            <Bx>
              <Btn variant="outlined" color="error" size="large" onClick={() => setAlertModalOpen(false)}>
                Abbrechen
              </Btn>
            </Bx>
            <Bx ml="auto" style={{ cursor: alertMessageSenderDisabled ? 'not-allowed' : 'pointer' }}>
              <Btn
                variant="contained"
                disabled={alertMessageSenderDisabled}
                color="success"
                size="large"
                onClick={() => {
                  alertClicked?.(alertType, statement);
                  setAlertModalOpen(false);
                  setAlertType('infringement');
                  setStatement('');
                  setConfirmationModalOpen(true);
                  setTimeout(() => {
                    setConfirmationModalOpen(false);
                  }, 3000);
                }}
              >
                Meldung abschicken
              </Btn>
            </Bx>
          </Bx>
        </Bx>
      </Anohana>
      <Anohana open={confirmationModalOpen} transition="zoom" disableScrollLock fullWidth maxWidth="sm" onClose={() => setConfirmationModalOpen(false)}>
        <Bx p={2.5}>
          <Bx justifyContent="center" mb={2}>
            <Bx pb={1}>
              <Typo variant="h4" component="span">
                Der Beitrag wurde erfolgreich gemeldet. Zur Bestätigung erhalten Sie eine E-Mail.
              </Typo>
            </Bx>
          </Bx>
          <Bx display="flex" mt={3}>
            <Bx ml="auto">
              <Btn
                variant="contained"
                color="success"
                size="large"
                onClick={() => {
                  setConfirmationModalOpen(false);
                }}
              >
                Ok
              </Btn>
            </Bx>
          </Bx>
        </Bx>
      </Anohana>
      <PinMessagePopup
        open={pinMessagePopupOpen}
        close={() => setPinMessagePopupOpen(false)}
        parentId={parentId}
        messageId={id}
        cancel={() => setPinMessagePopupOpen(false)}
      />
    </Bx>
  );
};

export interface IMessageDropdownProps {
  target?: any;
  close: () => void;

  isDeleted?: boolean;
  canDelete?: boolean;
  onDelete?: () => void;

  canEdit?: boolean;
  onEdit?: () => void;

  canQuote?: boolean;
  canThread?: boolean;
  onQuote?: () => void;
  onEmote: (emoji:string) => void;
  onAlert: () => void;
  isOwnMessage?: boolean;

  isAdmin?: boolean;
  messageId?: string;
  onPin?: () => void;
}

const useStyles = makeStyles(theme => ({
  contextMenu: {
    padding: 0
  },
  emojiCount: {
    lineHeight: 1.4,
    fontFamily: theme.typography.fontFamily,
    color: theme.palette.primary.main
  },
  emojiButton: {
    margin: 0,
    marginRight: '2px',
    padding: '2px',
    fontSize: 'inherit',
    borderRadius: 0,
    minWidth: 0,
    '&:hover': {
      backgroundColor: 'transparent'
    }
  },
  listUserEmoji: {
    paddingRight: '8px',
  },
  listUserName: {
    lineHeight: 1.4,
    fontFamily: theme.typography.fontFamily
  },
  emoteTeaser: {
    display: 'block',
    position: 'absolute',
    right: '10px',
    bottom: '-15px',
    margin: 0,
    borderWidth: '2px',
    borderStyle: 'solid',
    borderColor: theme.palette.primary.main,
    borderRadius: '5px',
    background: 'white',
    color: theme.palette.primary.main,
    padding: '0 5px',
    cursor: 'pointer',
  }
}));

export const MessageDropdown: FC<IMessageDropdownProps> = ({
  target,
  close,
  isDeleted,
  canDelete,
  onDelete,
  canEdit,
  onEdit,
  canQuote,
  canThread,
  onQuote,
  onEmote,
  onAlert,
  isOwnMessage,
  messageId,
  isAdmin = false,
  onPin
}) => {
  const classes = useStyles();
  if (!target) return <></>;
  return (
    <Menu className={clsx(classes.contextMenu)} anchorEl={target} keepMounted open={true} onClose={close}>
      {!isDeleted && canQuote && !!onEmote && <MenuItem key={'msg-drop-4'} onClick={() => onEmote('👍')}>
        <Btn className={clsx(classes.emojiButton)}>👍</Btn>
      </MenuItem>}
      {!isDeleted && canEdit && !!onEdit && <MenuItem key={'msg-drop-2'} onClick={onEdit}>Bearbeiten</MenuItem>}
      {!isDeleted && canQuote && !!onQuote && <MenuItem key={'msg-drop-3'} onClick={onQuote}>Kommentieren</MenuItem>}
      {!isDeleted && !isOwnMessage && <MenuItem key={'msg-drop-5'} onClick={onAlert}>Beitrag melden</MenuItem>}
      {!isDeleted && canDelete && !!onDelete && <MenuItem key={'msg-drop-1'} onClick={onDelete}>Löschen</MenuItem>}
      {!isDeleted && isAdmin && !!onPin && <MenuItem key={'msg-drop-9'} onClick={onPin}>Anheften</MenuItem>}
    </Menu>
  );
};

export const MessageEmoteTeaser = props => {
  const classes = useStyles();
  const emotes = Object.values(props.emotes) as string[];
  const emotesUnique = getUniqueArray(emotes);
  return (<>
      {(!!emotes && emotes.length > 0) && <div className={clsx(classes.emoteTeaser)}>
        <div onClick={e => props.openEmotes?.(e.currentTarget)}>
          {(emotesUnique && emotesUnique.length > 0) && emotesUnique.map((emoji, idx) => (
            <span key={`msg-emote-${idx}`}>{emoji}</span>
          ))}
          {emotes.length > 1 && <span className={clsx(classes.emojiCount)} key={'msg-emote-count'}>&nbsp;{emotes.length}</span>}
        </div>
      </div>}
    </>
  );
};

export interface IMessageEmotesProps {
  target?: any;
  close: () => void;

  emotes: {};
  activeParticipants?: any;
}

export const MessageEmotes: FC<IMessageEmotesProps> = ({ target, close, emotes, activeParticipants }) => {
  const classes = useStyles();
  const location = useLocation();
  if (!target) return <></>;
  return (
    <Menu className={clsx(classes.contextMenu)} anchorEl={target} keepMounted open={true} onClose={close}>
      {(emotes && Object.keys(emotes).length > 0) && Object.keys(emotes).map((user, idx) => {
        let userName = user;
        if (activeParticipants) {
          if (activeParticipants[user]) {
            userName = activeParticipants[user];
          }
        }
        return (
        <MenuItem key={`msg-emote-${idx}`}>
          <Link underline="none" to={getProfileRouteByUserId(user) + (location ? '?ref=' + location?.pathname : '')}>
            <span className={clsx(classes.listUserEmoji)}>{emotes[user]}</span><span className={clsx(classes.listUserName)}>{userName}</span>
          </Link>
        </MenuItem>
      )})}
    </Menu>
  );
};


export const MessageAlertTypeControl: React.FC<{
  alertId: string;
  alertName: string;
  alertType: string;
  setAlertType: (type: string) => void;
}> = ({
  alertId,
  alertName,
  alertType,
  setAlertType,
}) => {
  return <CheckboxControl
    control={{
      value: alertType === alertId,
      color: 'primary',
      onChange: e => {
        setAlertType(e.target.name);
      },
      name: alertId,
      style: {border: '1px solid black', borderRadius:'4px', padding:'0'}
    }}
    highlightLabel={true}
    checked={alertType === alertId}
    style={{background:'white',padding:'15px 15px 15px 15px',borderRadius:'10px 10px 10px 10px',border:1,borderStyle:'solid',borderColor:'#D6D7D9'}}
    label={<Typo>{alertName}</Typo>}
  />
};